'use client';

import { usePathname } from '@/i18n/navigation';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';

export default function ZapChat() {
  const [shouldLoad, setShouldLoad] = useState(false);
  const pathname = usePathname();
  const isHiddenRouteRef = useRef(false);

  const isHiddenRoute =
    pathname?.includes('/sora-2-ai-video-generator') ||
    pathname?.includes('/sora-2-pro-ai-video-generator') ||
    pathname?.includes('/nano-banana-2-ai-image-generator') ||
    pathname?.includes('/z-image-ai-image-generator') ||
    pathname?.includes('/runway-gen-4-ai-video-generator') ||
    pathname?.includes('/grok-imagine-ai-video-generator') ||
    pathname?.includes('/grok-imagine-ai-video-upscaler') ||
    pathname?.includes('/elevenlabs-sfx-ai-sound-generator') ||
    pathname?.includes('/zapvoice') ||
    pathname?.includes('/zaplead') ||
    pathname?.includes('/pricing') ||
    pathname?.includes('/admin') ||
    pathname?.includes('/user');

  // Keep ref in sync with state
  useEffect(() => {
    isHiddenRouteRef.current = isHiddenRoute;
  }, [isHiddenRoute]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoad(true);
    }, 4000); // Delay chat widget by 4 seconds
    return () => clearTimeout(timer);
  }, []);

  // Aggressive closure/hiding function — called via MutationObserver instead of setInterval
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!shouldLoad) return; // Only activate when widget is loaded

    // Use MutationObserver to watch for Typebot elements being added to the DOM
    // This is far more efficient than setInterval — only runs when DOM actually changes
    const hideChat = () => {
      if (!isHiddenRouteRef.current) return;

      // @ts-ignore
      const Typebot = window.Typebot;

      if (!document.getElementById('zapchat-hide-style')) {
        const style = document.createElement('style');
        style.id = 'zapchat-hide-style';
        style.innerHTML =
          'typebot-standard, typebot-bubble, #typebot-bubble { display: none !important; opacity: 0 !important; visibility: hidden !important; pointer-events: none !important; z-index: -9999 !important; }';
        document.head.appendChild(style);
      }

      document.body.classList.add('hide-typebot');

      if (Typebot) {
        try {
          Typebot.close();
          Typebot.hide();
        } catch (e) {
          // silently ignore
        }
      }

      const elements = document.querySelectorAll(
        'typebot-bubble, typebot-standard, #typebot-bubble'
      );
      elements.forEach((el) => {
        if ((el as HTMLElement).style.display !== 'none') {
          (el as HTMLElement).style.setProperty('display', 'none', 'important');
        }
      });
    };

    // Run immediately on activation
    hideChat();

    // Watch for Typebot elements being added to DOM — this fires only when needed
    const observer = new MutationObserver(() => {
      requestAnimationFrame(hideChat);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isHiddenRoute, shouldLoad]);

  if (!shouldLoad) return null;

  return (
    <Script
      src="https://cdn.jsdelivr.net/npm/@typebot.io/js@0/dist/web.js"
      strategy="lazyOnload"
      type="module"
      onLoad={() => {
        // @ts-ignore
        const Typebot = window.Typebot;
        if (!Typebot) return;

        // Array of messages to rotate through
        const messages = [
          'I have a question for you!',
          'Need help with something?',
          'How can I assist you today?',
          "Let's chat!",
          'Ask Me Anything!',
          'Got a moment for a quick question?',
        ];

        // Get a random message from the array
        function getRandomMessage() {
          const randomIndex = Math.floor(Math.random() * messages.length);
          return messages[randomIndex];
        }

        Typebot.initBubble({
          typebot: 'zap-chat-pt10p2u',
          apiHost: 'https://zapchat.zappingai.com',
          previewMessage: {
            avatarUrl: 'https://zappingai.com/images/jack-profile-color.png',
            message: getRandomMessage(),
            autoShowDelay: 8000,
          },
          theme: {
            button: { backgroundColor: '#4c51bf' },
            previewMessage: { backgroundColor: '#ffffff', textColor: '#1a1a1a', closeButtonIconColor: '#666460' },
            chatWindow: { backgroundColor: '#ffffff' },
          },
        });
      }}
    />
  );
}
